import React, {
  ChangeEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, Grid, Tab, Tabs } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import BasicTable, {
  Columns,
  cellStyles1,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/BasicTable';

import axios from 'axios';
import properties from '../../../core-utils/properties';
import Loader from '../../molecules/Loader';
import { patientAccessTheme } from '@HomaHealth/ui-component-library';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import {
  generateQuarterTabs,
  monthOptions,
} from '../../../core-utils/helper-utils/experienceHelper';
import { formatActivityDataExp } from '../../../core-utils/helper-utils/riskRegisterHelper';
import NoTabsData from '../../molecules/NoTabsData';
import TaskBoardNoData from '../../atoms/Icons/TaskBoardNoData';
import { DottedText } from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { useStores } from '../../../stores';
import NoContent from '../../atoms/Icons/NoContent';
import taskBoardConstants from '../../../core-utils/messages/TaskBoardConstants';
import RiskDetailsWindow from '../RiskDetailsWindow';
import useSnackbar from '../../../core-utils/custom-hooks/Snackbar';
import Snackbar from '../../atoms/Snackbar';
import { BasicModal as BasicModal1 } from '@HomaHealth/ui-component-library/lib/_AIQ-components';

type TaskBoardProps = {
  renewalMonth: string;
  incurredStartDate: any;
  incurredEndDate: any;
  paidStartDate: any;
  paidEndDate: any;
  isPlanDatesFetched: any;
  aggregationParameter: any;
  setIsEditOrg: React.Dispatch<React.SetStateAction<boolean>>;
};

const useStyles = makeStyles()({
  tab: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-start',
    textAlign: 'left',
    paddingLeft: '16px',
  },
  selectedTab: {
    backgroundColor: `${patientAccessTheme.palette.grey[100]}`,
  },
  quarterContainer: {
    marginTop: '10px',
    backgroundColor: '#fff',
    border: '1px solid #E5E9F0',
    borderRadius: 4,
    width: '100%',
    height: '90%',
  },
  tabsContainer: {
    borderBottom: '1px solid #e5e7eb',
  },
  tableConatiner: {
    maxHeight: '500px',
    overflowY: 'auto',
  },
  noDataContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '540px',
    border: '1px solid #E5E9F0',
    borderRadius: '4px',
  },
  noTableDataContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '480px',
  },
});

const ACTIVITY_TABLE_COLUMNS: Columns[] = [
  {
    name: 'id',
    cellStyles: cellStyles1,
    align: 'left',
    width: '150px',
  },
  {
    name: 'Name',
    cellStyles: cellStyles1,
    align: 'left',
    width: '300px',
  },
  { name: 'Category', cellStyles: cellStyles1, align: 'left', width: '300px' },
  { name: 'Status', cellStyles: cellStyles1, align: 'left', width: '200px' },
  { name: 'Owner', cellStyles: cellStyles1, align: 'left', width: '300px' },
];

const TaskBoard: React.FC<TaskBoardProps> = ({
  renewalMonth,
  incurredStartDate,
  incurredEndDate,
  paidStartDate,
  paidEndDate,
  aggregationParameter,
  setIsEditOrg,
}) => {
  const { classes } = useStyles();
  const [tableData, setTableData] = useState<any>([]);
  const [tablePageNumber, setTablePageNumber] = useState<number>(1);
  const [tableTotalPages, setTableTotalPages] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const tablePageSize = 6;
  const [mainLoader] = useState<any>(<Loader height="400px" />);
  // const quarterTabs = useMemo(() => generateQuarterTabs(renewalMonth), [
  //   renewalMonth,
  // ]);
  const today = new Date();
  const currentMonth = today.getMonth(); // August = 7
  const renewalMonthIndex = monthOptions.findIndex(
    (m) => m?.toLowerCase() === renewalMonth?.toLowerCase(),
  );

  // If the renewal month is in the future (e.g., Sep while today is Aug), use previous year
  const baseYear =
    renewalMonthIndex > currentMonth
      ? today.getFullYear() - 1
      : today.getFullYear();

  const quarterTabs = useMemo(
    () => generateQuarterTabs(renewalMonth, baseYear),
    [renewalMonth],
  );

  const [quarterIndex, setQuarterIndex] = useState<number | null>(null);
  const employerGroupStore = useStores().employerGroupStore;
  const hasSetQuarterIndex = useRef(false);
  const [taskDetailsWindowOpen, setTaskDetailsWindowOpen] = useState<boolean>(
    false,
  );
  const [selectedTask, setselectedTask] = useState<any>(null);
  const { snackbarState, openSnackbar, handleCloseSnackBar } = useSnackbar();
  const [taskDeletePopUpOpen, setTaskDeletePopUpOpen] = useState<boolean>(
    false,
  );
  const [selectedTaskId, setSelectedTaskId] = useState<string>('');

  // functions
  const handlePagination = (_event: ChangeEvent<unknown>, page: number) => {
    setTablePageNumber(page);
  };
  const handleTaskClick = (task: any) => {
    setselectedTask(task);
    setTaskDetailsWindowOpen(true);
  };

  const handleQuarterChange = (
    event: React.ChangeEvent<{}>,
    newValue: number,
  ) => {
    setQuarterIndex(newValue);
  };

  const handleMenuItem = (index: number, item: any) => {
    const taskId = tableData[index][0];
    switch (item) {
      case 'Delete':
        setSelectedTaskId(taskId);
        setTaskDeletePopUpOpen(true);
        break;
    }
  };
  const handleUpdateTask = async (data: any) => {
    const formData = new FormData();
    for (const key in data) {
      if (key === 'files') {
        continue;
      } // handle files separately below

      const value = data[key];

      if (Array.isArray(value)) {
        if (value.length && typeof value[0] === 'object') {
          // For arrays of objects like `activity`
          formData.append(key, JSON.stringify(value));
        } else {
          // For arrays of strings like `impact`, `owner`
          value.forEach((item: any, index: number) => {
            formData.append(`${key}[${index}]`, item);
          });
        }
      } else if (typeof value === 'object' && value !== null) {
        // In case any single object sneaks in
        formData.append(key, JSON.stringify(value));
      } else if (value !== '') {
        formData.append(key, value); // Only append non-empty values
      }
    }
    formData.append(
      'eg_nid',
      // 'crane_nxt_1742408999728',
      employerGroupStore.employerGroupResponseData.eg_nid[0],
    );
    formData.append(
      'employer',
      employerGroupStore.employerGroupResponseData.employer_group_name[0],
    );
    if (data.files && Array.isArray(data.files)) {
      data.files.forEach((file: File) => {
        formData.append('files', file);
      });
    }
    await axios.post(
      properties.aiqBaseUrl +
        properties.riskRegisterBaseUrl +
        properties.updateRisk,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    openSnackbar('Task has been updated', 'success', 6000);
    setTaskDetailsWindowOpen(false);
    getActivities();
  };
  const deleteTask = async () => {
    openSnackbar('Task deletion in progress', 'info', 2000);
    await axios
      .post(
        properties.aiqBaseUrl +
          properties.riskRegisterBaseUrl +
          properties.deleteActivity,
        {
          activity_id: selectedTaskId,
        },
      )
      .then((res: any) => {
        if (res.status === 200) {
          openSnackbar('Task has been deleted', 'success', 2000);
        }
      })
      .catch((err: any) => {
        openSnackbar('Task deletion failed.', 'error', 3000);
        throw err;
      });
  };

  const getActivities = async () => {
    try {
      if (quarterIndex === null) {
        return;
      }
      setLoading(true);
      await axios
        .post(
          properties.aiqBaseUrl +
            properties.riskRegisterBaseUrl +
            properties.getActivitiesByQuater,
          {
            eg_nid: employerGroupStore.employerGroupResponseData.eg_nid[0],
            page: tablePageNumber,
            limit: tablePageSize,
            // quarterMonths: quarterTabs[quarterIndex]?.months,
            quarterMonths: quarterTabs[quarterIndex]?.months || [],
          },
        )
        .then((res: any) => {
          if (res.status === 200) {
            const data = formatActivityDataExp(
              res?.data?.data?.activities,
              handleTaskClick,
            );
            setTableData(data);
            setTableTotalPages(res?.data?.data?.totalPages);
          }
        })
        .catch((error: any) => {
          console.error('Error in fetching taskbar data', error);
        });
    } catch (error) {
      console.error('Error in task board', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getActivities();
  }, [
    incurredStartDate,
    incurredEndDate,
    paidStartDate,
    paidEndDate,
    aggregationParameter,
    tablePageNumber,
    quarterIndex,
  ]);

  useEffect(() => {
    setTablePageNumber(1);
  }, [quarterIndex]);

  useEffect(() => {
    if (
      hasSetQuarterIndex.current ||
      !renewalMonth ||
      quarterTabs.length === 0
    ) {
      return;
    }
    const currentMonthIndex = new Date().getMonth();
    const currentMonthName = monthOptions[currentMonthIndex];
    // First try current month match
    const matchIndex = quarterTabs.findIndex((q) =>
       
      q.months.some(
         
        (m) => m.month?.toLowerCase() === currentMonthName?.toLowerCase(),
      ),
    );

    if (matchIndex !== -1) {
      setQuarterIndex(matchIndex);
    } else {
      // Fallback: derive from renewal month
      const renewalMonthIndex = monthOptions.findIndex(
        (m) => m?.toLowerCase() === renewalMonth?.toLowerCase(),
      );
      const fallbackIndex = Math.floor(renewalMonthIndex / 3);
      setQuarterIndex(fallbackIndex !== -1 ? fallbackIndex : 0);
    }
    hasSetQuarterIndex.current = true;
  }, [renewalMonth, quarterTabs]);

  const TabPanel = ({ children, value, index }: any) => {
    return value === index ? <Box>{children}</Box> : null;
  };

  return loading ? (
    mainLoader
  ) : (
    <Grid>
      {renewalMonth === '' ? (
        <Grid className={classes.noDataContainer}>
          <NoTabsData
            icon={<TaskBoardNoData />}
            label="Please enter your renewal month for the group to populate the task board !"
          />
          <div
            onClick={() => {
              setIsEditOrg(true);
            }}
          >
            <DottedText text="Enter Group Details" />
          </div>
        </Grid>
      ) : (
        <Grid
          container
          className={classes.quarterContainer}
          sx={{
            flexDirection: 'column',
          }}
        >
          <Grid className={classes.tabsContainer} size={12}>
            <Tabs
              value={quarterIndex}
              onChange={handleQuarterChange}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
              style={{}}
            >
              {quarterTabs?.map((tab, idx) => (
                <Tab
                  key={idx}
                  className={classes.tab}
                  classes={{ selected: classes.selectedTab }}
                  label={
                    <Typography
                      fontSize="12px"
                      styleProps={{
                        color: idx === quarterIndex ? '#2E2F33' : '#596180',
                        textAlign: 'left',
                      }}
                    >
                      {tab.label}
                    </Typography>
                  }
                />
              ))}
            </Tabs>
          </Grid>
          {quarterTabs?.map((tab, idx) => {
            return (
              <TabPanel value={quarterIndex} index={idx} key={tab.label}>
                <Grid className={classes.tableConatiner}>
                  {tableData?.length > 0 ? (
                    <BasicTable
                      isMenuAvailable={true}
                      handleCellMenuItem={handleMenuItem}
                      menuOptions={taskBoardConstants.MENU_OPTIONS}
                      displayType="row"
                      columns={ACTIVITY_TABLE_COLUMNS}
                      rowsData={tableData}
                      showPagination={true}
                      handlePagination={handlePagination}
                      pageCount={tableTotalPages}
                      pageNumber={tablePageNumber}
                      rootStyles={{
                        borderBottom: 'none',
                      }}
                      hideColumnIndex={0}
                      tableBodyStyles={{
                        borderBottom: 'none',
                      }}
                      isFullTable={true}
                    />
                  ) : (
                    <Grid className={classes.noTableDataContainer}>
                      <NoTabsData
                        icon={<NoContent />}
                        label="No task to show"
                      />
                    </Grid>
                  )}
                </Grid>
              </TabPanel>
            );
          })}
        </Grid>
      )}
      {taskDetailsWindowOpen && (
        <Grid
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
          onClick={() => setTaskDetailsWindowOpen(false)}
        >
          <div
            style={{ height: '100%', backgroundColor: '#fff' }}
            onClick={(e) => e.stopPropagation()}
          >
            <RiskDetailsWindow
              activity={selectedTask}
              setDetailsWindowOpen={setTaskDetailsWindowOpen}
              handleSave={handleUpdateTask}
              handleOpenSnackBar={openSnackbar}
              isActivityWindow={true}
            />
          </div>
        </Grid>
      )}
      {taskDeletePopUpOpen && (
        <BasicModal1
          open={taskDeletePopUpOpen}
          title={taskBoardConstants.DELETE}
          body={
            <Typography
              fontSize="12px"
              lineHeight="17px"
              fontType="medium"
              textColor={patientAccessTheme.palette.text.highemphasis}
              margin="0px 0px 15px 0px"
            >
              {taskBoardConstants.DELETE_DESC}
            </Typography>
          }
          cancelButtonLabel={taskBoardConstants.CANCEL}
          confirmButtonLabel={taskBoardConstants.CONFIRM}
          handleCancel={() => setTaskDeletePopUpOpen(false)}
          handleConfirm={() => {
            deleteTask();
            setTaskDeletePopUpOpen(false);
          }}
          handleClose={() => setTaskDeletePopUpOpen(false)}
        />
      )}
      <Snackbar
        {...snackbarState}
        handleClose={handleCloseSnackBar}
        vertical="bottom"
        horizontal="right"
      />
    </Grid>
  );
};

export default TaskBoard;
